import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import DropDownField from "../../DropDownField";
import MultiDropDownField from "../../MultiDropDown";

const DiaryForm = ({ mode = "add" }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);

  const [students, setStudents] = useState([]);
  const [selectedStudentNames, setSelectedStudentNames] = useState([]);

  const [formData, setFormData] = useState({
    guid: "",
    date: new Date().toISOString().split("T")[0],
    branch_guid: "",
    class_guid: "",
    homework: "",
    classwork: "",
    tomorrow_activities: "",
    teacher_remarks: "",
    target_type: "class",
    student_guids: "",
  });

  const role = localStorage.getItem("role") || "staff";
  const userGuid = localStorage.getItem("guid") || "";
  const prefixPath = role === "admin" ? "/admin" : "/teacher";

  useEffect(() => {
    fetchBranches();
    fetchClasses();
    if (mode === "edit" && id) {
      fetchDiaryDetails();
    }
  }, [mode, id]);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(apiEndpoints.branches, {
        headers: getHeaders(),
      });
      if (res.data && res.data.data) {
        setBranches(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${apiEndpoints.class}?action=getAll`, {
        headers: getHeaders(),
      });
      if (res.data && res.data.status) {
        setClasses(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  };

  const fetchStudents = async (classGuid) => {
    try {
      const res = await axios.get(
        `${apiEndpoints.student_diary}?action=getStudents&class_guid=${classGuid}`,
        { headers: getHeaders() }
      );
      if (res.data && res.data.status) {
        setStudents(res.data.data || []);
        return res.data.data || [];
      }
    } catch (err) {
      console.error("Error fetching students:", err);
    }
    return [];
  };

  const handleClassChange = async (classGuid) => {
    setFormData((prev) => ({ ...prev, class_guid: classGuid, student_guids: "", target_type: "class" }));
    setSelectedStudentNames([]);
    if (classGuid) {
      await fetchStudents(classGuid);
    } else {
      setStudents([]);
    }
  };

  const handleStudentSelection = (names) => {
    setSelectedStudentNames(names);
    const selectedGuids = names
      .map((name) => students.find((s) => s.name === name)?.guid)
      .filter(Boolean)
      .join(",");
    setFormData((prev) => ({ ...prev, student_guids: selectedGuids }));
  };

  const fetchDiaryDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiEndpoints.student_diary}?guid=${id}`, {
        headers: getHeaders(),
      });
      if (res.data && res.data.status && res.data.data) {
        const d = res.data.data;
        // Verify permissions
        if (role === "staff" && d.created_by !== userGuid) {
          showSnackbar("Unauthorized: You can only edit your own diary entries.", "error");
          navigate(`${prefixPath}/diary`);
          return;
        }
        const classStudents = await fetchStudents(d.class_guid);
        let selectedNames = [];
        if (d.target_type === "students" && d.student_guids) {
          const guidsList = d.student_guids.split(",");
          selectedNames = classStudents
            .filter((s) => guidsList.includes(s.guid))
            .map((s) => s.name);
        }
        setSelectedStudentNames(selectedNames);

        setFormData({
          guid: d.guid,
          date: d.date,
          branch_guid: d.branch_guid,
          class_guid: d.class_guid,
          homework: d.homework || "",
          classwork: d.classwork || "",
          tomorrow_activities: d.tomorrow_activities || "",
          teacher_remarks: d.teacher_remarks || "",
          target_type: d.target_type || "class",
          student_guids: d.student_guids || "",
        });
      } else {
        showSnackbar("Failed to fetch diary details", "error");
        navigate(`${prefixPath}/diary`);
      }
    } catch (err) {
      console.error("Error fetching diary:", err);
      showSnackbar("Error loading diary details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.date || !formData.branch_guid || !formData.class_guid) {
      showSnackbar("Please fill in all required fields (Date, Branch, Class)", "warning");
      return;
    }

    setSaving(true);
    try {
      let res;
      if (mode === "edit") {
        res = await axios.put(apiEndpoints.student_diary, formData, {
          headers: getHeaders(),
        });
      } else {
        res = await axios.post(apiEndpoints.student_diary, formData, {
          headers: getHeaders(),
        });
      }

      if (res.data && res.data.status) {
        showSnackbar(
          mode === "edit"
            ? "Diary entry updated successfully"
            : "Diary entry created successfully",
          "success"
        );
        navigate(`${prefixPath}/diary`);
      } else {
        showSnackbar(res.data.message || "Failed to save diary entry", "error");
      }
    } catch (err) {
      console.error("Error saving diary:", err);
      showSnackbar("Error saving diary entry", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.95)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)"
        }}
      >
        {/* Header with back button */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton 
            onClick={() => navigate(`${prefixPath}/diary`)} 
            sx={{ 
              mr: 2, 
              backgroundColor: "rgba(63, 81, 181, 0.08)",
              color: "#3F51B5",
              "&:hover": {
                backgroundColor: "rgba(63, 81, 181, 0.15)",
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={700} color="#3F51B5">
            {mode === "edit" ? "Edit Student Diary" : "Create Daily Student Diary"}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Metadata Group */}
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  p: 3, 
                  borderRadius: 3, 
                  backgroundColor: "#F4F6F9",
                  border: "1px solid #E2E8F0"
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      required
                      label="Diary Date"
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ sx: { borderRadius: 2, backgroundColor: "#fff" } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropDownField
                      label="School Branch"
                      value={formData.branch_guid}
                      onChange={(val) =>
                        setFormData((prev) => ({ ...prev, branch_guid: val }))
                      }
                      options={branches.map((b) => ({
                        guid: b.guid,
                        name: b.name,
                      }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropDownField
                      label="Target Class"
                      value={formData.class_guid}
                      onChange={handleClassChange}
                      options={classes.map((c) => ({
                        guid: c.guid,
                        name: c.name,
                      }))}
                      required
                    />
                  </Grid>

                  {formData.class_guid && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <DropDownField
                          label="Target Audience"
                          value={formData.target_type}
                          onChange={(val) =>
                            setFormData((prev) => ({ 
                              ...prev, 
                              target_type: val,
                              student_guids: val === "class" ? "" : prev.student_guids 
                            }))
                          }
                          options={[
                            { guid: "class", name: "Whole Class" },
                            { guid: "students", name: "Specific Students" }
                          ]}
                          required
                        />
                      </Grid>
                      {formData.target_type === "students" && (
                        <Grid item xs={12} sm={6}>
                          <MultiDropDownField
                            label="Select Students"
                            value={selectedStudentNames}
                            options={students.map((s) => s.name)}
                            onChange={handleStudentSelection}
                          />
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </Box>
            </Grid>

            {/* Daily Logs Group */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
                Academic Log
              </Typography>
              <TextField
                fullWidth
                label="Classwork Completed"
                name="classwork"
                value={formData.classwork}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="Log the topics and classwork completed today..."
                InputProps={{ sx: { borderRadius: 3 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
                Assignments
              </Typography>
              <TextField
                fullWidth
                label="Homework Assigned"
                name="homework"
                value={formData.homework}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="Specify the homework or assignments for today..."
                InputProps={{ sx: { borderRadius: 3 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
                Future Activities
              </Typography>
              <TextField
                fullWidth
                label="Tomorrow's Activities"
                name="tomorrow_activities"
                value={formData.tomorrow_activities}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="List any special events, materials, or plans for tomorrow..."
                InputProps={{ sx: { borderRadius: 3 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
                Teacher Guidance
              </Typography>
              <TextField
                fullWidth
                label="Remarks & General Notes"
                name="teacher_remarks"
                value={formData.teacher_remarks}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="Enter any reminders, remarks, or notes for the parents..."
                InputProps={{ sx: { borderRadius: 3 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12} display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(`${prefixPath}/diary`)}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)" }}
              >
                {saving ? "Saving..." : "Save Entry"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default DiaryForm;
